/* eslint-disable @typescript-eslint/no-explicit-any */
import { LocalDateTime } from 'js-joda';

import { core } from '@tock/coretypes';
import { handlePasscodeErrorsInModal } from 'utils/passcodeUtils';
import AppConstants from 'constants/AppConstants';
import protos from 'utils/protos';

const { HoldTicketsRequest, TicketGroup, TicketSubsetDeltaRequest, TicketSubset } = protos;

export function add(ticketGroup: core.TicketGroup, passcode?: string): ApiAction {
  return {
    TockApi: {
      postProto: '/ticket/group',
      type: AppConstants.CALENDAR_ACTION,
      request: TicketGroup.fromObject(ticketGroup),
      toast: 'Reservation added',
      requestScope: {
        passcode,
      },
      onError: {
        ...handlePasscodeErrorsInModal,
        defaultToOriginal: true,
      },
    },
  };
}

export function update(
  ticketGroup: core.TicketGroup,
  passcode?: string,
  toast = 'Reservation updated'
): ApiAction {
  return {
    TockApi: {
      putProto: '/ticket/group',
      type: AppConstants.CALENDAR_ACTION,
      request: TicketGroup.fromObject(ticketGroup),
      toast: toast,
      requestScope: {
        passcode,
      },
      onError: {
        ...handlePasscodeErrorsInModal,
        defaultToOriginal: true,
      },
    },
  };
}

export function remove(ticketGroup: core.TicketGroup): ApiAction {
  return {
    TockApi: {
      putProto: `/admin/ticket/group/archive/${ticketGroup.id}`,
      type: AppConstants.CALENDAR_ACTION,
      toast: 'Reservation removed',
    },
  };
}

export function cancelEmailHandSell(purchaseVerifyId: number, type: string): ApiAction {
  return {
    TockApi: {
      getProto: `/admin/purchase/cancel-verify-email?purchaseVerifyId=${purchaseVerifyId}`,
      type: AppConstants.CALENDAR_ACTION,
      toast: `Book by ${type} was canceled`,
    },
  };
}

export function resendEmailHandSell(purchaseVerifyId: number, type: string): ApiAction {
  return {
    TockApi: {
      getProto: `/admin/purchase/resend-verify-email?purchaseVerifyId=${purchaseVerifyId}`,
      type: AppConstants.CALENDAR_ACTION,
      toast: `Purchase ${type} was sent to the patron`,
    },
  };
}

export function updateHoldForCommunalTable(
  ticketGroupId: number,
  numAdditionalTicket: number
): ApiAction {
  return {
    TockApi: {
      postProto: '/ticket/subset/delta',
      type: AppConstants.CALENDAR_ACTION,
      request: TicketSubsetDeltaRequest.fromObject({
        ticketGroupId,
        numAdditionalTicket,
      }),
      toast: 'Updated holds for table',
    },
  };
}

interface HoldOptions {
  endDateTimeExclusive: LocalDateTime;
  startDateTime: LocalDateTime;
  tableId: number;
}

export function holdTimeRangeForTable(ticketGroupId: number, options: HoldOptions): ApiAction {
  const { startDateTime, endDateTimeExclusive: endDateTime, tableId } = options;
  return {
    TockApi: {
      putProto: '/ticket/group/hold',
      type: AppConstants.CALENDAR_ACTION,
      request: HoldTicketsRequest.fromObject({
        endDateTime,
        startDateTime,
        tableId: [tableId],
        ticketGroupId,
      }),
      toast: 'Held table',
    },
  };
}

export function unholdSubset(heldSubsetId: number): ApiAction {
  return {
    TockApi: {
      deleteProto: `/ticket/group/hold/${heldSubsetId}`,
      type: AppConstants.CALENDAR_ACTION,
      toast: 'Table unheld',
    },
  };
}

export function holdAvailableTicketsForPatron(
  ticketGroupId: number,
  heldForPatron: any,
  ticketCount: number
): ApiAction {
  return {
    TockApi: {
      postProto: '/ticket/subset',
      type: AppConstants.CALENDAR_ACTION,
      request: TicketSubset.fromObject({
        ticketCount,
        ticketGroupId,
        heldForPatron,
        state: TicketSubset.State.HELD,
      }),
      toast: 'Created hold for a guest',
    },
  };
}

export function holdHeldTicketsForPatron(
  ticketSubsetId: number,
  ticketGroupId: number,
  heldForPatron: any
): ApiAction {
  return {
    TockApi: {
      putProto: '/ticket/subset',
      type: AppConstants.CALENDAR_ACTION,
      request: TicketSubset.fromObject({
        id: ticketSubsetId,
        ticketGroupId,
        heldForPatron,
        state: TicketSubset.State.HELD,
      }),
      toast: 'Created hold for a guest',
    },
  };
}

export function removeHeldForPatron(ticketSubsetId: number, ticketGroupId: number): ApiAction {
  return {
    TockApi: {
      putProto: '/ticket/subset',
      type: AppConstants.CALENDAR_ACTION,
      request: TicketSubset.fromObject({
        id: ticketSubsetId,
        ticketGroupId,
        state: TicketSubset.State.HELD,
      }),
      toast: 'Hold deleted',
    },
  };
}
